
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    ChevronDown,
    ChevronRight,
    Play,
    GitBranch,
    ArrowRight,
    Clock,
    Layers,
    Repeat,
    CheckCircle,
    XCircle,
    Bot,
    Wrench,
    Brain,
    Workflow,
    Pin,
    PinOff,
    Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ToolboxItem {
    type: string;
    label: string;
    icon: React.ReactNode;
    description: string;
    color: string;
    resource?: string;
    resourceType?: 'agent' | 'tool' | 'model' | 'workflow' | 'policy';
}

interface ToolboxSection {
    title: string;
    icon: React.ReactNode;
    color: string;
    items: ToolboxItem[];
}

const aslStateTypes: ToolboxItem[] = [
    { type: 'task', label: 'Task', icon: <Play size={16} />, description: 'Execute a capability', color: 'text-blue-600' },
    { type: 'newAgent', label: 'Agent Task', icon: <Bot size={16} />, description: 'Create new agent task', color: 'text-violet-600', resourceType: 'agent' },
    { type: 'newTool', label: 'Tool Task', icon: <Wrench size={16} />, description: 'Create new tool task', color: 'text-indigo-600', resourceType: 'tool' },
    { type: 'choice', label: 'Choice', icon: <GitBranch size={16} />, description: 'Conditional branching', color: 'text-amber-600' },
    { type: 'pass', label: 'Pass', icon: <ArrowRight size={16} />, description: 'Pass data through', color: 'text-slate-600' },
    { type: 'wait', label: 'Wait', icon: <Clock size={16} />, description: 'Wait for duration', color: 'text-orange-600' },
    { type: 'fanOut', label: 'Fan-out', icon: <Layers size={16} />, description: 'Parallel fork (AND)', color: 'text-purple-600' },
    { type: 'fanIn', label: 'Fan-in', icon: <Layers size={16} />, description: 'Parallel join', color: 'text-purple-600' },
    { type: 'map', label: 'Map', icon: <Repeat size={16} />, description: 'Iterate over items', color: 'text-emerald-600' },
    { type: 'succeed', label: 'Succeed', icon: <CheckCircle size={16} />, description: 'Success terminal', color: 'text-green-600' },
    { type: 'fail', label: 'Fail', icon: <XCircle size={16} />, description: 'Failure terminal', color: 'text-red-600' },
];

interface WorkflowToolboxProps {
    isOpen: boolean;
    onClose: () => void;
    agents?: Array<{ name: string; fqcn: string; description?: string }>;
    tools?: Array<{ name: string; fqcn: string; description?: string }>;
    models?: Array<{ name: string; fqcn: string; description?: string }>;
    workflows?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    policies?: Array<{ name: string; fqcn: string; description?: string }>;
    isPinned?: boolean;
    onPinToggle?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

export function WorkflowToolbox({
    isOpen,
    onClose,
    agents = [],
    tools = [],
    models = [],
    workflows = [],
    policies = [],
    isPinned = false,
    onPinToggle,
    onMouseEnter,
    onMouseLeave,
}: WorkflowToolboxProps) {
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(['asl-states', 'agents'])
    );

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => {
            const next = new Set(prev);
            if (next.has(section)) {
                next.delete(section);
            } else {
                next.add(section);
            }
            return next;
        });
    };

    const onDragStart = (
        event: React.DragEvent, 
        type: string, 
        resource?: string,
        resourceName?: string,
        resourceType?: 'agent' | 'tool' | 'model' | 'workflow' | 'policy'
    ) => {
        event.dataTransfer.setData('application/reactflow-type', type);
        if (resource) {
            event.dataTransfer.setData('application/reactflow-resource', resource);
        }
        if (resourceName) {
            event.dataTransfer.setData('application/reactflow-resource-name', resourceName);
        }
        if (resourceType) {
            event.dataTransfer.setData('application/reactflow-resource-type', resourceType);
        }
        event.dataTransfer.effectAllowed = 'move';
    };

    const sections: ToolboxSection[] = [
        {
            title: 'ASL States',
            icon: <GitBranch size={16} />,
            color: 'text-blue-600',
            items: aslStateTypes,
        },
        {
            title: 'Agents',
            icon: <Bot size={16} />,
            color: 'text-violet-600',
            items: agents.map((a) => ({
                type: 'task',
                label: a.name,
                icon: <Bot size={16} />,
                description: a.description || 'Agent capability',
                color: 'text-violet-600',
                resource: `agent:${a.fqcn}`,
                resourceType: 'agent' as const,
            })),
        },
        {
            title: 'Tools',
            icon: <Wrench size={16} />,
            color: 'text-indigo-600',
            items: tools.map((t) => ({
                type: 'task',
                label: t.name,
                icon: <Wrench size={16} />,
                description: t.description || 'Tool capability',
                color: 'text-indigo-600',
                resource: `tool:${t.fqcn}`,
                resourceType: 'tool' as const,
            })),
        },
        {
            title: 'Models',
            icon: <Brain size={16} />,
            color: 'text-pink-600',
            items: models.map((m) => ({
                type: 'task',
                label: m.name,
                icon: <Brain size={16} />,
                description: m.description || 'LLM model',
                color: 'text-pink-600',
                resource: `model:${m.fqcn}`,
                resourceType: 'model' as const,
            })),
        },
        {
            title: 'Workflows',
            icon: <Workflow size={16} />,
            color: 'text-cyan-600',
            items: workflows.map((w) => ({
                type: 'task',
                label: w.name,
                icon: <Workflow size={16} />,
                description: w.description || `Sub-workflow: ${w.fqcn ?? w.name}`,
                color: 'text-cyan-600',
                resource: `workflow:${w.fqcn ?? w.name}`,
                resourceType: 'workflow' as const,
            })),
        },
        {
            title: 'Policies',
            icon: <Shield size={16} />,
            color: 'text-emerald-600',
            items: policies.map((p) => ({
                type: 'policy',
                label: p.name,
                icon: <Shield size={16} />,
                description: p.description || 'Policy capability',
                color: 'text-emerald-600',
                resource: `policy:${p.fqcn}`,
                resourceType: 'policy' as const,
            })),
        },
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ x: '100%', opacity: 0.8 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: '100%', opacity: 0 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    className={cn(
                        "flex h-full w-72 flex-col border-l border-slate-200 bg-white shadow-xl",
                        !isPinned && "absolute right-0 top-0 z-20"
                    )}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                        <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">Toolbox</h3>
                            {!isPinned && (
                                <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                    hover
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {onPinToggle && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={onPinToggle}
                                    title={isPinned ? 'Unpin toolbox' : 'Pin toolbox open'}
                                    className={cn(
                                        'h-7 w-7 p-0',
                                        isPinned && 'text-blue-600 bg-blue-50'
                                    )}
                                >
                                    {isPinned ? <PinOff size={14} /> : <Pin size={14} />}
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
                                <X size={16} />
                            </Button>
                        </div>
                    </div>

                    {/* Sections */}
                    <div className="flex-1 overflow-y-auto">
                        {sections.map((section) => (
                            <div key={section.title} className="border-b border-slate-100">
                                <button
                                    onClick={() => toggleSection(section.title.toLowerCase().replace(' ', '-'))}
                                    className="flex w-full items-center gap-2 px-4 py-2.5 transition-colors hover:bg-slate-50"
                                >
                                    <span className={section.color}>{section.icon}</span>
                                    <span className="flex-1 text-left text-sm font-medium text-slate-700">
                                        {section.title}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                        {section.items.length}
                                    </span>
                                    <span className="text-slate-400">
                                        {expandedSections.has(section.title.toLowerCase().replace(' ', '-')) ? (
                                            <ChevronDown size={16} />
                                        ) : (
                                            <ChevronRight size={16} />
                                        )}
                                    </span>
                                </button>

                                {expandedSections.has(section.title.toLowerCase().replace(' ', '-')) && (
                                    <div className="space-y-1 px-2 pb-2">
                                        {section.items.length === 0 ? (
                                            <p className="px-2 py-2 text-xs text-slate-400">No items available</p>
                                        ) : (
                                            section.items.map((item, idx) => (
                                                <div
                                                    key={`${item.type}-${idx}`}
                                                    draggable
                                                    onDragStart={(e) => onDragStart(e, item.type, item.resource, item.label, item.resourceType)}
                                                    className={cn(
                                                        'flex cursor-grab items-center gap-2 rounded-md border border-slate-200 px-3 py-2',
                                                        'bg-white transition-all hover:border-slate-300 hover:bg-slate-50',
                                                        'active:cursor-grabbing'
                                                    )}
                                                >
                                                    <span className={item.color}>{item.icon}</span>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="truncate text-sm font-medium text-slate-700">
                                                            {item.label}
                                                        </p>
                                                        <p className="truncate text-xs text-slate-400">
                                                            {item.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Footer hint */}
                    <div className="border-t border-slate-200 bg-slate-50 px-4 py-3">
                        <p className="text-xs text-slate-500">
                            Drag items onto the graph to add them
                        </p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

